import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { cn } from '../../lib/utils'
import { CHART_TOOLTIP_STYLE } from '../../utils/stats-utils'
import { cellKey, planCategories, categoryColorMap, type MatrixState } from './matrix-helpers'

interface PlanRadarChartProps {
  matrix: MatrixState
}

/**
 * Radar of total work minutes per category across the whole plan. Category
 * colours (the legend squares) match the matrix squares and the minutes area
 * chart. Clicking a legend entry drops that category from the radar, so a
 * category that dwarfs the others can be set aside to compare the rest.
 */
export function PlanRadarChart({ matrix }: PlanRadarChartProps) {
  const { t } = useTranslation()
  const colorMap = useMemo(() => categoryColorMap(matrix), [matrix])
  const [hiddenIds, setHiddenIds] = useState<string[]>([])

  function toggleCategory(id: string) {
    setHiddenIds((prev) => (prev.includes(id) ? prev.filter((hidden) => hidden !== id) : [...prev, id]))
  }

  const data = useMemo(
    () =>
      planCategories(matrix).map((category) => {
        let minutes = 0
        for (const row of matrix.rows) {
          if (row.exercise.category?.id !== category.id) continue
          for (const week of matrix.weeks) {
            minutes += (matrix.frequencies[cellKey(week.week_number, row.exercise_id)] ?? 0) *
              (row.exercise.duration_minutes ?? 0)
          }
        }
        return { id: category.id, category: category.name, minutes, color: colorMap.get(category.id) ?? '#94a3b8' }
      }),
    [matrix, colorMap]
  )

  const totalMinutes = data.reduce((sum, point) => sum + point.minutes, 0)
  const visible = data.filter((point) => !hiddenIds.includes(point.id))
  if (data.length === 0 || totalMinutes === 0) return null

  return (
    <div className="rounded-lg border p-3">
      <p className="text-sm font-medium mb-2">{t('trainingPlans.builder.radarChartTitle')}</p>
      {visible.length === 0 ? (
        <div className="flex h-[260px] items-center justify-center text-xs text-muted-foreground">
          {t('trainingPlans.builder.noCategoriesShown')}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <RadarChart data={visible} outerRadius="68%">
            <PolarGrid />
            <PolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis tick={false} axisLine={false} />
            <Tooltip {...CHART_TOOLTIP_STYLE} />
            <Radar
              name={t('trainingPlans.builder.radarChartTitle')}
              dataKey="minutes"
              stroke="#94a3b8"
              fill="#94a3b8"
              fillOpacity={0.25}
            />
          </RadarChart>
        </ResponsiveContainer>
      )}

      <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1">
        {data.map((point) => {
          const hidden = hiddenIds.includes(point.id)
          return (
            <button
              key={point.id}
              type="button"
              aria-pressed={!hidden}
              title={t('trainingPlans.builder.toggleCategory')}
              onClick={() => toggleCategory(point.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted',
                hidden && 'opacity-40'
              )}
            >
              <span
                className="inline-block size-3 rounded-sm"
                style={{ backgroundColor: hidden ? 'transparent' : point.color, boxShadow: `inset 0 0 0 1px ${point.color}` }}
                aria-hidden="true"
              />
              <span className={cn(hidden && 'line-through')}>{point.category}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
